import * as DOMPurifyModule from "dompurify";

// dompurify uses `export =` (CJS). esModuleInterop is disabled in this tsconfig,
// so the namespace import is cast to the module's own type to get .sanitize()
// typings. Mirrors the interop cast in DocxPreviewRenderer.tsx.
// TODO(PF-344): Simplify when esModuleInterop is enabled project-wide.
const DOMPurify = DOMPurifyModule as unknown as typeof import("dompurify");

/**
 * Tags stripped from stored rich-text content before render. Scoped to vectors with
 * no legitimate editor use that enable the reported exploits: meta refresh (the
 * reproduced no-interaction redirect), script (also blocked by the editor itself),
 * form/input/etc. (credential harvesting), object (obsolete Flash embedding), and
 * link/base (document-level redirect/URL hijack). iframe/svg/math/video are NOT
 * stripped — they are legitimate editor output; we strip their event-handler
 * attributes instead.
 */
const FORBIDDEN_TAGS = [
    "script", "meta", "form", "input", "textarea", "button", "select",
    "object", "link", "base",
];

/** Inline event handlers (the redirect-via-handler vector) and formaction. */
const FORBIDDEN_ATTRS = [
    "onerror", "onload", "onclick", "onmouseover", "onmouseout", "onmousedown",
    "onmouseup", "onfocus", "onblur", "onsubmit", "onreset", "onchange",
    "oninput", "onkeydown", "onkeyup", "onkeypress", "onscroll", "onwheel",
    "onanimationstart", "onanimationend", "onanimationiteration",
    "ontransitionend", "onbeforeunload", "onhashchange", "onpopstate",
    "onpointerdown", "onpointerup", "onpointermove", "ondrag", "ondrop",
    "formaction",
];

const SANITIZE_CONFIG = {
    FORBID_TAGS: FORBIDDEN_TAGS,
    FORBID_ATTR: FORBIDDEN_ATTRS,
    // Editor-authored elements outside DOMPurify's default allowlist, plus the
    // permissioned-content custom element.
    ADD_TAGS: ["perm-content", "iframe", "svg", "math", "video", "source"],
    ADD_ATTR: [
        "role-id", "target", "container-id", "class",
        "allow", "allowfullscreen", "frameborder", "width", "height", "src", "controls",
    ],
    ALLOW_DATA_ATTR: false,
};

// Permissioned-content markers (`<amzn1.abacus.team.ROLE>...`) look like tags but
// are not valid custom elements; DOMPurify would unwrap them to their inner text.
// Protect them with inert placeholders across the sanitize pass, then restore.
const PERM_MARKER_REGEX = /<\/?amzn1\.abacus\.team\.[^>]*?>/g;
const PERM_PLACEHOLDER_PREFIX = "x9permmarker9x";
const PERM_PLACEHOLDER_SUFFIX = "x9endmarker9x";
const PERM_PLACEHOLDER_RESTORE = /x9permmarker9x(\d+)x9endmarker9x/g;

/**
 * Render-time backstop that removes XSS/redirect/embedding vectors from stored
 * rich-text HTML before it is passed to dangerouslySetInnerHTML. Complements the
 * server-side write-time sanitization; either alone stops the exploit, and having
 * both also neutralizes any content stored before write-time sanitization existed.
 */
export function sanitizeRichTextForRender(html: string): string {
    if (html == null || html === "") {
        return html;
    }
    // DOMPurify returns "" when no DOM is available (SSR / test env without jsdom).
    // In that case fall back to the raw content rather than blanking the view.
    if (!DOMPurify.isSupported) {
        return html;
    }

    const preservedMarkers: string[] = [];
    const protectedHtml = html.replace(PERM_MARKER_REGEX, (match) => {
        const token = `${PERM_PLACEHOLDER_PREFIX}${preservedMarkers.length}${PERM_PLACEHOLDER_SUFFIX}`;
        preservedMarkers.push(match);
        return token;
    });

    let clean = DOMPurify.sanitize(protectedHtml, SANITIZE_CONFIG) as unknown as string;

    if (preservedMarkers.length > 0) {
        clean = clean.replace(PERM_PLACEHOLDER_RESTORE, (match, index) => {
            const original = preservedMarkers[Number(index)];
            return original !== undefined ? original : match;
        });
    }

    return clean;
}
